import { useTranslation } from 'react-i18next';
import { BadgeProps } from '@tq/ui-components/dist/components/badge';
import { spacing } from '@tq/ui-components/dist/components/mobile-options-accordion/styles/theme/spacing';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

import { CUSTOM_COLOURS } from 'styles';
import { ApplicationStatus } from 'types';

type UseApplicationStatusReturn = {
  [key in ApplicationStatus]: ApplicationStatusItem;
};

export type ApplicationStatusItem = {
  label: string;
  badgeProps: BadgeProps;
};

const getStatusItem = (
  labelText: string,
  colour?: string,
  bgColour?: string,
  width?: string
): ApplicationStatusItem => {
  return {
    label: labelText,
    badgeProps: {
      color: colour ?? 'inherit',
      bgColor: bgColour ?? 'inherit',
      width: width ?? STANDARD_WIDTH,
    },
  };
};

export const STANDARD_WIDTH = spacing(24.5);

export const UnknownStatusItem = (): ApplicationStatusItem => {
  const { t } = useTranslation('application_status');
  return getStatusItem(
    t('not_available', 'Not available'),
    COLOURS.black,
    COLOURS.white,
    STANDARD_WIDTH
  );
};

export const useApplicationStatuses = (): UseApplicationStatusReturn => {
  const { t } = useTranslation('application_status');

  return {
    Valid: getStatusItem(t('valid', 'Valid'), COLOURS.black, COLOURS.blue[300]),
    'Consultation Open': getStatusItem(
      t('consultation_open', 'Consultation Open'),
      undefined,
      COLOURS.blue[600],
      spacing(34)
    ),
    'Site Inspection Complete': getStatusItem(
      t('site_inspection_complete', 'Site Inspection Complete'),
      undefined,
      COLOURS.blue[600],
      spacing(44)
    ),
    'Under Consideration': getStatusItem(
      t('under_consideration', 'Under Consideration'),
      COLOURS.black,
      CUSTOM_COLOURS.lightGreen,
      spacing(36)
    ),
    'Decision Pending': getStatusItem(
      t('decision_pending', 'Decision Pending'),
      COLOURS.black,
      CUSTOM_COLOURS.lightGreen,
      spacing(32)
    ),
    'Notice of Opinion Issued': getStatusItem(
      t('notice_of_opinion_issued', 'Notice of Opinion Issued'),
      undefined,
      CUSTOM_COLOURS.mediumGreen,
      spacing(44)
    ),
    Determined: getStatusItem(
      t('determined', 'Determined'),
      undefined,
      CUSTOM_COLOURS.mediumGreen
    ),
    'Appeal in Progress': getStatusItem(
      t('appeal_in_progress', 'Appeal in Progress'),
      undefined,
      COLOURS.yellow[500],
      spacing(35)
    ),
    'Public Local Inquiry - Referred to PAC': getStatusItem(
      t('public_local_inquiry_referred_to_pac', 'Public Local Inquiry - Referred to PAC'),
      undefined,
      COLOURS.yellow[500],
      spacing(63)
    ),
    'Hearing - Referred to PAC': getStatusItem(
      t('hearing_referred_to_pac', 'Hearing - Referred to PAC'),
      undefined,
      COLOURS.yellow[500],
      spacing(45)
    ),
    'Judicial Review in Progress': getStatusItem(
      t('judicial_review_in_progress', 'Judicial Review in Progress'),
      undefined,
      COLOURS.yellow[500],
      spacing(48)
    ),
    Revoked: getStatusItem(t('revoked', 'Revoked'), undefined, CUSTOM_COLOURS.mediumGreen),
    Withdrawn: getStatusItem(
      t('withdrawn', 'Withdrawn'),
      undefined,
      CUSTOM_COLOURS.mediumGreen
    ),
  };
};
